import React, { createContext, useContext, useEffect, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useUser } from '@/providers/UserProvider'
import { useSocketConnection } from '@/hooks/queries/subscription/socket'
import { useDispatchSocket, useDispatchEmit } from '@/hooks/queries/subscription/dispatch'
import { dispatchSocket, accountingSocket, stockSocket, invoicingSocket } from '@/utils/socket'
import { isManager } from '@/utils/role'
import type {
  DispatchUpdatedEvent,
  DispatchEmitPayload,
} from '@/types'
import type {
  AccountingSocketEvent,
  CustomerChangedEvent,
  WeeklySettingsChangedEvent,
  InvoiceChangedEvent,
} from '@/types'

interface SocketContextType {
  isConnected: boolean
  lastMessage: DispatchUpdatedEvent | null
  emitDispatchUpdate: (data: DispatchEmitPayload) => void
  emitEmployeeUpdate: (data: { employeeId: number; changes: Record<string, unknown>; updatedBy?: string }) => void
  onDispatchUpdate: (callback: (data: DispatchUpdatedEvent) => void) => () => void
  onEmployeeUpdate: (callback: (data: DispatchUpdatedEvent & { type: 'employee_update' | 'employee_updated' }) => void) => () => void
  onContractChanged: (callback: (data: AccountingSocketEvent) => void) => () => void
  onCustomerChanged: (callback: (data: CustomerChangedEvent) => void) => () => void
  onExpenseChanged: (callback: (data: AccountingSocketEvent) => void) => () => void
  onTransferChanged: (callback: (data: AccountingSocketEvent) => void) => () => void
  onInvoiceChanged: (callback: (data: InvoiceChangedEvent) => void) => () => void
  onRestockFormUpdated: (callback: (data: Record<string, unknown>) => void) => () => void
  emitRestockFormUpdate: (data: Record<string, unknown>) => void
}

const SocketContext = createContext<SocketContextType | undefined>(undefined)

export const useSocketContext = () => {
  const context = useContext(SocketContext)
  if (!context) {
    throw new Error('useSocketContext must be used within a SocketProvider')
  }
  return context
}

interface SocketProviderProps {
  children: ReactNode
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const queryClient = useQueryClient()
  const { user } = useUser()
  const socketConnection = useSocketConnection()
  const dispatchSocketHook = useDispatchSocket()
  const dispatchEmit = useDispatchEmit()

  // Manage accounting namespace lifecycle alongside dispatch (managers only).
  useEffect(() => {
    if (!isManager(user?.role)) {
      if (accountingSocket.isConnected()) {
        accountingSocket.disconnect()
      }
      return () => {}
    }

    accountingSocket.connect()
    const unsubConnect = accountingSocket.on('connect', () => {
      accountingSocket.emit('joinAccounting')
    })
    const unsubJoined = accountingSocket.on('joinedAccounting', () => {})
    if (accountingSocket.isConnected()) {
      accountingSocket.emit('joinAccounting')
    }
    const unsubWeeklyData = accountingSocket.on('weeklyDataChanged', () => {
      queryClient.invalidateQueries({ queryKey: ['accounting', 'weekly-data'], exact: false })
    })
    const unsubWeeklySettings = accountingSocket.on<WeeklySettingsChangedEvent>('weeklySettingsChanged', (_data) => {
      queryClient.invalidateQueries({ queryKey: ['accounting', 'weekly-settings'], exact: false })
    })

    return () => {
      unsubConnect()
      unsubJoined()
      unsubWeeklyData()
      unsubWeeklySettings()
      accountingSocket.disconnect()
    }
  }, [queryClient, user?.role])

  // Manage stock namespace lifecycle for Restock form synchronization
  useEffect(() => {
    stockSocket.connect()
    const unsubConnect = stockSocket.on('connect', () => {
      stockSocket.emit('joinStock')
    })
    const unsubJoined = stockSocket.on('joinedStock', () => {})
    if (stockSocket.isConnected()) {
      stockSocket.emit('joinStock')
    }

    return () => {
      unsubConnect()
      unsubJoined()
      stockSocket.disconnect()
    }
  }, [])

  // Manage invoicing namespace lifecycle (all users : employés + managers).
  useEffect(() => {
    invoicingSocket.connect()
    const unsubConnect = invoicingSocket.on('connect', () => {
      invoicingSocket.emit('joinInvoicing')
    })
    const unsubJoined = invoicingSocket.on('joinedInvoicing', () => {})
    if (invoicingSocket.isConnected()) {
      invoicingSocket.emit('joinInvoicing')
    }

    return () => {
      unsubConnect()
      unsubJoined()
      invoicingSocket.disconnect()
    }
  }, [])

  const value: SocketContextType = {
    isConnected: socketConnection.isConnected,
    lastMessage: dispatchSocketHook.lastDispatchMessage,
    emitDispatchUpdate: dispatchEmit.emitDispatchUpdate,
    emitEmployeeUpdate: (data) => {
      dispatchSocket.emit('dispatchUpdate', { type: 'employee_update', data })
    },
    onDispatchUpdate: dispatchSocketHook.onDispatchUpdate,
    onEmployeeUpdate: (callback) => {
      return dispatchSocket.on<DispatchUpdatedEvent>('dispatchUpdated', (msg) => {
        if (msg.type === 'employee_update' || msg.type === 'employee_updated') {
          callback(msg as DispatchUpdatedEvent & { type: 'employee_update' | 'employee_updated' })
        }
      })
    },
    onContractChanged: (callback) => {
      return accountingSocket.on<AccountingSocketEvent>('contractChanged', callback)
    },
    onCustomerChanged: (callback) => {
      return accountingSocket.on<CustomerChangedEvent>('customerChanged', callback)
    },
    onExpenseChanged: (callback) => {
      return accountingSocket.on<AccountingSocketEvent>('expenseChanged', callback)
    },
    onTransferChanged: (callback) => {
      return accountingSocket.on<AccountingSocketEvent>('transferChanged', callback)
    },
    onInvoiceChanged: (callback) => {
      return invoicingSocket.on<InvoiceChangedEvent>('invoiceChanged', callback)
    },
    onRestockFormUpdated: (callback) => {
      return stockSocket.on('restockFormUpdated', callback)
    },
    emitRestockFormUpdate: (data) => {
      stockSocket.emit('restockFormUpdate', data)
    },
  }

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  )
}
